/**
 * PlanCarousel Component
 * プランを左右にスライドして表示するカルーセルコンポーネント
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Plan, Location } from '../types/core';
import { PlanCard } from './PlanCard';

export interface PlanCarouselProps {
    plans: Plan[];
    startLocation?: Location;
    onShare?: (plan: Plan) => void;
    className?: string;
}

export const PlanCarousel: React.FC<PlanCarouselProps> = ({
    plans,
    startLocation,
    onShare,
    className = ''
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const carouselRef = useRef<HTMLDivElement>(null);
    const mobileScrollRef = useRef<HTMLDivElement>(null);
    const touchStartX = useRef<number>(0);
    const touchEndX = useRef<number>(0);
    const isDragging = useRef<boolean>(false);
    const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

    // 無限ループ用のプラン配列を作成（前後に複製を追加）
    const extendedPlans = useMemo(() => {
        if (plans.length <= 1) return plans;
        return [...plans, ...plans, ...plans]; // 3回複製で無限ループ
    }, [plans]);

    // 実際の開始インデックス（中央のセット）
    const actualStartIndex = plans.length;

    // スクロール位置からインデックスを計算
    const calculateIndexFromScroll = useCallback(() => {
        if (!mobileScrollRef.current) return 0;

        const scrollLeft = mobileScrollRef.current.scrollLeft;
        const cardWidth = window.innerWidth * 0.75 + window.innerWidth * 0.05; // 75vw + 5vw margin
        const scrollIndex = Math.round(scrollLeft / cardWidth);

        // 実際のプランインデックスに変換
        const actualIndex = scrollIndex % plans.length;
        return actualIndex;
    }, [plans.length]);

    // プログラムによるスクロール
    const scrollToIndex = useCallback((index: number, immediate = false) => {
        if (!mobileScrollRef.current) return;

        const cardWidth = window.innerWidth * 0.75 + window.innerWidth * 0.05;
        // 中央のセットの位置を計算
        const scrollLeft = (actualStartIndex + index) * cardWidth;

        mobileScrollRef.current.scrollTo({
            left: scrollLeft,
            behavior: immediate ? 'auto' : 'smooth'
        });
    }, [actualStartIndex]);

    // 無限ループの位置調整
    const adjustInfiniteLoop = useCallback(() => {
        if (!mobileScrollRef.current || plans.length <= 1) return;

        const scrollLeft = mobileScrollRef.current.scrollLeft;
        const cardWidth = window.innerWidth * 0.75 + window.innerWidth * 0.05;
        const scrollIndex = scrollLeft / cardWidth;

        // 最初のセットにいる場合、最後のセットに移動
        if (scrollIndex < plans.length * 0.5) {
            const newScrollLeft = (scrollIndex + plans.length * 2) * cardWidth;
            mobileScrollRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'auto'
            });
        }
        // 最後のセットにいる場合、最初のセットに移動
        else if (scrollIndex > plans.length * 2.5) {
            const newScrollLeft = (scrollIndex - plans.length * 2) * cardWidth;
            mobileScrollRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'auto'
            });
        }
    }, [plans.length]);

    // スクロールイベントハンドラー
    const handleScroll = useCallback(() => {
        if (isTransitioning) return;

        // 既存のタイムアウトをクリア
        if (scrollTimeout.current) {
            clearTimeout(scrollTimeout.current);
        }

        // スクロール終了を検出
        scrollTimeout.current = setTimeout(() => {
            const newIndex = calculateIndexFromScroll();
            if (newIndex !== currentIndex) {
                setCurrentIndex(newIndex);
            }
            // 無限ループの位置調整
            adjustInfiniteLoop();
        }, 150);
    }, [calculateIndexFromScroll, currentIndex, isTransitioning, adjustInfiniteLoop]);

    // 前のプランに移動
    const goToPrevious = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        const newIndex = currentIndex === 0 ? plans.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);

        // モバイルの場合はスクロール
        if (window.innerWidth < 768) {
            scrollToIndex(newIndex);
        }

        setTimeout(() => setIsTransitioning(false), 300);
    };

    // 次のプランに移動
    const goToNext = () => {
        if (isTransitioning) return;
        setIsTransitioning(true);
        const newIndex = currentIndex === plans.length - 1 ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);

        // モバイルの場合はスクロール
        if (window.innerWidth < 768) {
            scrollToIndex(newIndex);
        }

        setTimeout(() => setIsTransitioning(false), 300);
    };

    // 特定のプランに移動
    const goToIndex = (index: number) => {
        if (isTransitioning || index === currentIndex) return;
        setIsTransitioning(true);
        setCurrentIndex(index);

        // モバイルの場合はスクロール
        if (window.innerWidth < 768) {
            scrollToIndex(index);
        }

        setTimeout(() => setIsTransitioning(false), 300);
    };

    // 初期スクロール位置設定
    useEffect(() => {
        if (mobileScrollRef.current && plans.length > 1) {
            // 中央のセットの最初の位置にスクロール
            scrollToIndex(currentIndex, true);
        }
    }, [scrollToIndex, currentIndex, plans.length]);

    // キーボードナビゲーション
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                goToPrevious();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                goToNext();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, plans.length]);

    // タッチイベント処理（iPhone最適化版）
    const handleTouchStart = (e: React.TouchEvent) => {
        if (isTransitioning || plans.length <= 1) return;
        touchStartX.current = e.targetTouches[0].clientX;
        touchEndX.current = e.targetTouches[0].clientX;
        isDragging.current = true;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging.current || isTransitioning) return;
        touchEndX.current = e.targetTouches[0].clientX;

        // 横スワイプの場合は縦スクロールを防ぐ
        const distance = Math.abs(touchStartX.current - touchEndX.current);
        if (distance > 15) {
            e.preventDefault();
        }
    };

    const handleTouchEnd = () => {
        if (!isDragging.current || isTransitioning) return;
        isDragging.current = false;

        const distance = touchStartX.current - touchEndX.current;
        const isLeftSwipe = distance > 30;
        const isRightSwipe = distance < -30;

        if (isLeftSwipe) {
            goToNext();
        } else if (isRightSwipe) {
            goToPrevious();
        }

        touchStartX.current = 0;
        touchEndX.current = 0;
    };

    if (plans.length === 0) {
        return null;
    }

    return (
        <div className={`w-full ${className}`}>
            {/* デスクトップ用ナビゲーション（矢印ボタン） */}
            {plans.length > 1 && (
                <div className="hidden md:flex justify-between items-center mb-4 px-2">
                    {/* 前へボタン */}
                    <button
                        onClick={goToPrevious}
                        disabled={isTransitioning}
                        className="bg-white hover:bg-gray-50 rounded-full p-3 shadow-lg transition-all duration-200 disabled:opacity-50 border border-gray-200 flex-shrink-0"
                        aria-label="前のプラン"
                    >
                        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* プラン情報 */}
                    <div className="text-center flex-1 mx-4">
                        <p className="text-sm font-medium text-gray-700">
                            プラン {currentIndex + 1} / {plans.length}
                        </p>
                    </div>

                    {/* 次へボタン */}
                    <button
                        onClick={goToNext}
                        disabled={isTransitioning}
                        className="bg-white hover:bg-gray-50 rounded-full p-3 shadow-lg transition-all duration-200 disabled:opacity-50 border border-gray-200 flex-shrink-0"
                        aria-label="次のプラン"
                    >
                        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            )}

            {/* スマートフォン用プラン情報 */}
            {plans.length > 1 && (
                <div className="md:hidden mb-4 text-center">
                    <p className="text-sm font-medium text-gray-700">
                        プラン {currentIndex + 1} / {plans.length}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                        左右にスワイプで切り替え
                    </p>
                </div>
            )}

            {/* デスクトップ用カルーセルコンテナ（フル幅） */}
            <div className="hidden md:block relative overflow-hidden rounded-lg">
                <div
                    ref={carouselRef}
                    className="flex transition-transform duration-300 ease-out"
                    style={{
                        transform: `translateX(-${currentIndex * 100}%)`,
                    }}
                >
                    {plans.map((plan, index) => (
                        <div
                            key={plan.id}
                            className="w-full flex-shrink-0"
                            style={{ minWidth: '100%' }}
                        >
                            <div className="px-2">
                                <PlanCard
                                    plan={plan}
                                    planIndex={index}
                                    startLocation={startLocation}
                                    onShare={onShare}
                                    className="w-full h-full"
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* スマートフォン用カルーセルコンテナ（改善されたピーク表示） */}
            <div className="md:hidden">
                <div
                    ref={mobileScrollRef}
                    className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
                    style={{
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        WebkitOverflowScrolling: 'touch'
                    }}
                    onScroll={handleScroll}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {extendedPlans.map((plan, index) => {
                        const actualIndex = index % plans.length;
                        const isFirst = index === 0;
                        const isLast = index === extendedPlans.length - 1;

                        return (
                            <div
                                key={`${plan.id}-${index}`}
                                className="flex-shrink-0 snap-center"
                                style={{
                                    width: '75vw',
                                    marginLeft: isFirst ? '12.5vw' : '5vw',
                                    marginRight: isLast ? '12.5vw' : '0'
                                }}
                            >
                                <div className={`transition-all duration-300 ${actualIndex === currentIndex
                                    ? 'scale-100 opacity-100'
                                    : 'scale-95 opacity-70'
                                    }`}>
                                    <PlanCard
                                        plan={plan}
                                        planIndex={actualIndex}
                                        startLocation={startLocation}
                                        onShare={onShare}
                                        className="w-full h-full"
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* インジケーター */}
            {plans.length > 1 && (
                <div className="flex justify-center mt-6 space-x-2">
                    {plans.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToIndex(index)}
                            disabled={isTransitioning}
                            className={`w-3 h-3 rounded-full transition-all duration-200 ${index === currentIndex
                                ? 'bg-blue-600 scale-110'
                                : 'bg-gray-300 hover:bg-gray-400'
                                }`}
                            aria-label={`プラン ${index + 1} に移動`}
                        />
                    ))}
                </div>
            )}

            {/* デスクトップ用操作ガイド */}
            {plans.length > 1 && (
                <div className="hidden md:block text-center mt-4">
                    <p className="text-xs text-gray-500">
                        矢印キーまたはボタンで切り替え
                    </p>
                </div>
            )}
        </div>
    );
};

export default PlanCarousel;